/**
 * Robust clipboard copy that works across all contexts:
 *  - Modern HTTPS pages → uses navigator.clipboard.writeText
 *  - Non-secure / iframe / older browsers → falls back to a hidden <textarea> + document.execCommand("copy")
 * Returns true on success, false on failure.
 */
import { toast } from "sonner";

export async function copyToClipboard(text, { successMessage = "Copied!", silent = false } = {}) {
  if (!text) {
    if (!silent) toast.error("Nothing to copy");
    return false;
  }

  // 1) Try the modern async clipboard API (requires HTTPS + user gesture)
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      if (!silent) toast.success(successMessage);
      return true;
    }
  } catch {
    // fall through to legacy
  }

  // 2) Fallback: hidden textarea + execCommand("copy") — works on HTTP + older browsers
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "0";
    ta.style.left = "0";
    ta.style.opacity = "0";
    ta.style.pointerEvents = "none";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    ta.setSelectionRange(0, ta.value.length);
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    if (ok) {
      if (!silent) toast.success(successMessage);
      return true;
    }
  } catch {
    /* ignore */
  }

  // 3) Last resort — show the text in a prompt so the user can copy manually
  if (!silent) {
    toast.error("Auto-copy blocked — long-press the URL below to copy");
    try {
      window.prompt("Copy this URL (Ctrl/Cmd + C):", text);
    } catch {
      /* ignore */
    }
  }
  return false;
}
